import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { SOCIAL_PLATFORMS, emptySocialLinks, emptySocialEnabled, normalizeSocialLink } from '../../utils/socialLinks';
import './admin-social-links.css';

export default function AdminSocialLinks() {
  const [links, setLinks] = useState(emptySocialLinks);
  const [saved, setSaved] = useState(emptySocialLinks);
  const [enabled, setEnabled] = useState(emptySocialEnabled);
  const [savedEnabled, setSavedEnabled] = useState(emptySocialEnabled);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = async () => {
    setLoading(true); setError('');
    try {
      const { data } = await axios.get('/api/admin/site/socials');
      const next = { ...emptySocialLinks(), ...data.links };
      const nextEnabled = { ...emptySocialEnabled(), ...data.enabled };
      setLinks(next);
      setSaved(next);
      setEnabled(nextEnabled);
      setSavedEnabled(nextEnabled);
    } catch (err) { setError(err.response?.data?.message || 'Could not load social links.'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async event => {
    event.preventDefault();
    if (saving) return;
    const normalizedLinks = emptySocialLinks();
    for (const { id } of SOCIAL_PLATFORMS) {
      const result = normalizeSocialLink(id, links[id]);
      if (result.error) { setError(result.error); setNotice(''); return; }
      normalizedLinks[id] = result.href;
    }
    setSaving(true); setError(''); setNotice('');
    try {
      const { data } = await axios.put('/api/admin/site/socials', { links: normalizedLinks, enabled });
      const next = { ...emptySocialLinks(), ...data.links };
      const nextEnabled = { ...emptySocialEnabled(), ...data.enabled };
      setLinks(next);
      setSaved(next);
      setEnabled(nextEnabled);
      setSavedEnabled(nextEnabled);
      setNotice('Social links updated.');
      window.dispatchEvent(new Event('deenlit-social-links-updated'));
    } catch (err) { setError(err.response?.data?.message || 'Could not save social links.'); }
    finally { setSaving(false); }
  };
  const changed = SOCIAL_PLATFORMS.some(({ id }) => links[id] !== saved[id] || enabled[id] !== savedEnabled[id]);
  const savedLinkCount = SOCIAL_PLATFORMS.filter(({ id }) => !!links[id]?.trim()).length;
  const shownCount = SOCIAL_PLATFORMS.filter(({ id }) => !!links[id]?.trim() && enabled[id]).length;

  return <div className="admin-page">
    <header className="admin-page-head">
      <h1 className="admin-page-title">Social links</h1>
      <p className="admin-page-sub">Add your DeenLit account URLs. Linked icons appear in the footer and on the Contact page.</p>
    </header>

    {error && <div className="admin-card admin-social-alert" role="alert">{error}{loading && <button type="button" className="admin-btn admin-btn-ghost admin-btn-sm" onClick={load}>Retry</button>}</div>}
    {notice && <div className="admin-card admin-social-notice" role="status">{notice}</div>}

    <form className="admin-card" onSubmit={save}>
      <h2>DeenLit accounts</h2>
      <p className="admin-social-intro">Paste an account or channel link to show its icon. Switch it off to hide the icon without losing the URL.</p>
      <p className="admin-social-summary">{shownCount} {shownCount === 1 ? 'icon' : 'icons'} set to show in the footer and on Contact.{savedLinkCount > 0 && shownCount === 0 ? ' Your saved links are currently hidden. Switch on the icons you want to show, then save.' : ''}</p>
      <div className="admin-social-list">
        {SOCIAL_PLATFORMS.map(({ id, label, placeholder }) => <div className="admin-social-row" key={id}>
          <div className="admin-social-heading">
            <label className="admin-label" htmlFor={`social-${id}`}>{label}</label>
            <label className="admin-social-switch">
              <input type="checkbox" aria-label={`Show ${label} icon`} checked={!!enabled[id]} disabled={loading || saving || !links[id]?.trim()} onChange={event => { setEnabled(current => ({ ...current, [id]: event.target.checked })); setNotice(''); }} />
              <span className="admin-social-switch-track" aria-hidden="true" />
              <span className="admin-social-switch-text">{enabled[id] ? 'Shown' : 'Hidden'}</span>
            </label>
          </div>
          <div className="admin-social-input-row">
            <input
              id={`social-${id}`}
              className="admin-input"
              type="text"
              inputMode="url"
              maxLength={300}
              placeholder={placeholder}
              value={links[id] || ''}
              disabled={loading || saving}
              onChange={event => { const value = event.target.value; setLinks(current => ({ ...current, [id]: value })); setEnabled(current => ({ ...current, [id]: !!value.trim() })); setError(''); setNotice(''); }}
            />
            {links[id] && <button type="button" className="admin-btn admin-btn-ghost admin-btn-sm" disabled={loading || saving} onClick={() => { setLinks(current => ({ ...current, [id]: '' })); setEnabled(current => ({ ...current, [id]: false })); setNotice(''); }}>Clear</button>}
          </div>
        </div>)}
      </div>
      <div className="admin-social-actions">
        <button className="admin-btn" type="submit" disabled={loading || saving || !changed}>{saving ? 'Saving...' : 'Save social links'}</button>
        <button className="admin-btn admin-btn-ghost" type="button" disabled={loading || saving || !changed} onClick={() => { setLinks({ ...saved }); setEnabled({ ...savedEnabled }); setError(''); }}>Undo changes</button>
      </div>
    </form>
  </div>;
}
